import { Amplify } from 'aws-amplify';
import { post, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsExports from '../aws-exports';

// Configure Amplify with proper API settings
export function configureAmplifyApi() {
  try {
    Amplify.configure(awsExports);
    console.log('Amplify API configured successfully');
  } catch (error) {
    console.error('Error configuring Amplify API:', error);
  }
}

// Helper to check if error is authentication-related
export const isAuthError = (error) => {
  const authErrorPatterns = [
    'NotAuthorizedException',
    'UnauthorizedException', 
    'Unauthenticated access',
    'Missing Authentication Token',
    'MissingAuthenticationTokenException',
    'ExpiredTokenException',
    'InvalidTokenException',
    'Token has expired',
    'No current user',
    'not authenticated'
  ];
  
  const errorStr = error?.message || error?.toString() || '';
  return authErrorPatterns.some(pattern => 
    errorStr.toLowerCase().includes(pattern.toLowerCase())
  ) || error?.response?.statusCode === 401 || error?.response?.statusCode === 403;
};

// Helper to validate session before making API calls
export const validateSession = async () => {
  try {
    const session = await fetchAuthSession();
    
    // Check if we have valid tokens
    if (!session?.tokens?.accessToken) {
      throw new Error('No valid access token');
    }
    
    // Check if token is expired
    const accessToken = session.tokens.accessToken;
    const exp = accessToken.payload?.exp;
    
    if (exp) {
      const now = Math.floor(Date.now() / 1000);
      if (now >= exp) {
        throw new Error('Token has expired');
      }
    }
    
    return session;
  } catch (error) {
    console.error('Session validation failed:', error);
    // Dispatch a custom event to trigger logout
    window.dispatchEvent(new CustomEvent('authError', { 
      detail: { error: 'Session expired or invalid' } 
    }));
    throw error;
  }
};

// Helper function to make API requests with proper error handling
export const apiPost = async (apiName, path, body, token) => {
  try {
    const response = await post({
      apiName,
      path,
      options: {
        body,
        headers: {
          Authorization: token
        }
      }
    }).response;

    const { body: responseBody, statusCode } = response;

    // Log the raw response for debugging
    console.log('API Response:', {
      statusCode,
      body: responseBody
    });

    // Check for error status codes first
    if (statusCode >= 500) {
      throw new Error('Server error occurred');
    }

    if (statusCode >= 400) {
      let errorMessage = 'Request failed';
      try {
        // For AWS Amplify errors, try to get the error from the response directly
        if (response.error) {
          throw new Error(response.error);
        }

        // Get the raw text
        const rawText = await responseBody.text();
        console.log('Raw error response:', rawText);
        
        try {
          const parsedBody = JSON.parse(rawText);
          if (parsedBody.error || parsedBody.message) {
            throw new Error(parsedBody.error || parsedBody.message);
          }
        } catch (jsonError) {
          if (rawText) {
            throw new Error(rawText);
          }
        }
      } catch (e) {
        throw new Error(e.message || errorMessage);
      }
      throw new Error(errorMessage);
    }

    // For successful responses, parse and return the body with success flag
    if (responseBody) {
      try {
        // Get the raw text first
        const rawText = await responseBody.text();
        console.log('Raw success response text:', rawText);
        
        // If empty response but successful status
        if (!rawText) {
          return { success: true, message: 'Operation successful' };
        }

        // Try to parse as JSON
        try {
          const parsedBody = JSON.parse(rawText);
          // If it's a successful response but has an error field, treat it as an error
          if (parsedBody.error) {
            throw new Error(parsedBody.error);
          }
          return { ...parsedBody, success: true };
        } catch (jsonError) {
          console.warn('Failed to parse response as JSON:', jsonError);
          return { success: true, message: rawText };
        }
      } catch (e) {
        if (e.message) {
          throw e; // Re-throw if it's our error with a message
        }
        console.warn('Failed to read response:', e);
        return { success: true, message: 'Operation successful' };
      }
    }

    // If no body but successful status, return success
    return { success: true, message: 'Operation successful' };

  } catch (error) {
    console.log('API Error:', error);

    // Check if this is an authentication error
    if (isAuthError(error)) {
      console.error('Authentication error detected, triggering logout');
      window.dispatchEvent(new CustomEvent('authError', { 
        detail: { error: error.message || 'Authentication failed' } 
      }));
      throw new Error('Authentication failed. Please log in again.');
    }

    // Helper function to extract error message from potentially nested JSON
    const extractErrorMessage = (errorData) => {
      if (typeof errorData === 'string') {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(errorData);
          return extractErrorMessage(parsed);
        } catch (e) {
          // If it's not JSON, return as is
          return errorData;
        }
      } else if (typeof errorData === 'object' && errorData !== null) {
        // If it's an object, look for error or message properties
        return errorData.error ? extractErrorMessage(errorData.error) : (errorData.message || 'Unknown error');
      }
      return errorData?.toString() || 'Unknown error';
    };

    // Handle AWS Amplify specific errors
    if (error.name === 'UnknownError') {
      // Try to get error details from the response
      if (error.response) {
        try {
          // If response is already parsed
          if (error.response.error) {
            throw new Error(extractErrorMessage(error.response.error));
          }
          
          // Try to get error from response body
          if (error.response.body) {
            const errorMessage = extractErrorMessage(error.response.body);
            throw new Error(errorMessage);
          }

          // If statusCode is available, include it in error
          if (error.response.statusCode) {
            const statusMessage = error.response.statusCode >= 500 ? 'Server error occurred' : 'Request failed';
            throw new Error(statusMessage);
          }
        } catch (e) {
          throw new Error(extractErrorMessage(e.message));
        }
      }
    }

    // If the error already has a message from our status code checks or previous error handling, pass it through
    if (error.message) {
      throw new Error(extractErrorMessage(error.message));
    }

    // For network or other errors
    throw new Error('Network error occurred');
  }
};

// Helper function to make GET API requests with proper error handling
export const apiGet = async (apiName, path, token, queryParams) => {
  try {
    const options = {
      headers: {
        Authorization: token
      }
    };
    if (queryParams) {
      options.queryParams = queryParams;
    }
    const response = await get({
      apiName,
      path,
      options
    }).response;

    const { body: responseBody, statusCode } = response;

    // Log the raw response for debugging
    console.log('API GET Response:', {
      statusCode,
      body: responseBody
    });

    // Check for error status codes first
    if (statusCode >= 500) {
      throw new Error('Server error occurred');
    }

    if (statusCode >= 400) {
      let errorMessage = 'Request failed';
      try {
        if (response.error) {
          throw new Error(response.error);
        }

        const rawText = await responseBody.text();
        console.log('Raw error response:', rawText);
        
        try {
          const parsedBody = JSON.parse(rawText);
          if (parsedBody.error || parsedBody.message) {
            throw new Error(parsedBody.error || parsedBody.message);
          }
        } catch (jsonError) {
          if (rawText) {
            throw new Error(rawText);
          }
        }
      } catch (e) {
        throw new Error(e.message || errorMessage);
      }
      throw new Error(errorMessage);
    }

    // For successful responses, parse and return the body with success flag
    if (responseBody) {
      try {
        const rawText = await responseBody.text();
        console.log('Raw success response text:', rawText);
        
        if (!rawText) {
          return { success: true, message: 'Operation successful' };
        }

        try {
          const parsedBody = JSON.parse(rawText);
          if (parsedBody.error) {
            throw new Error(parsedBody.error);
          }
          return { ...parsedBody, success: true };
        } catch (jsonError) {
          console.warn('Failed to parse response as JSON:', jsonError);
          return { success: true, message: rawText };
        }
      } catch (e) {
        if (e.message) {
          throw e;
        }
        console.warn('Failed to read response:', e);
        return { success: true, message: 'Operation successful' };
      }
    }

    return { success: true, message: 'Operation successful' };

  } catch (error) {
    console.log('API GET Error:', error);

    if (isAuthError(error)) {
      console.error('Authentication error detected, triggering logout');
      window.dispatchEvent(new CustomEvent('authError', { 
        detail: { error: error.message || 'Authentication failed' } 
      }));
      throw new Error('Authentication failed. Please log in again.');
    }

    if (error.message) {
      throw new Error(error.message);
    }

    throw new Error('Network error occurred');
  }
};

// Initialize on import
configureAmplifyApi(); 